import { createTheme } from '@mui/material/styles';
import { commonStyles } from './styles';

// Dark theme colors
const darkColors = {
  primary: {
    main: '#06B6D4',
    light: '#0891B2',
    dark: '#0E7490',
  },
  secondary: {
    main: '#7C3AED',
    light: '#8B5CF6',
    dark: '#6D21A8',
  },
  accent: {
    main: '#D946EF',
    light: '#E879F9',
    dark: '#C026D3',
  },
  background: {
    main: 'radial-gradient(1200px 800px at 20% 30%, #1e2a78 0%, #0b0f2b 50%, #0a0d20 100%)',
    glass: 'rgba(255, 255, 255, 0.05)',
    glassHover: 'rgba(255, 255, 255, 0.08)',
    glassStrong: 'rgba(255, 255, 255, 0.1)',
  },
  text: {
    primary: 'rgba(255, 255, 255, 0.9)',
    secondary: 'rgba(255, 255, 255, 0.7)',
    muted: 'rgba(255, 255, 255, 0.6)',
    disabled: 'rgba(255, 255, 255, 0.3)',
  },
  border: {
    light: 'rgba(255, 255, 255, 0.1)',
    medium: 'rgba(255, 255, 255, 0.15)',
    strong: 'rgba(255, 255, 255, 0.2)',
  },
  gradient: {
    primary: 'linear-gradient(90deg, #3B82F6 0%, #1D4ED8 48%, #06B6D4 100%)',
    primaryHover: 'linear-gradient(90deg, #2563EB 0%, #1E40AF 48%, #0891B2 100%)',
  },
  warning: {
    main: '#F59E0B',
    light: '#FBBF24',
    dark: '#D97706',
  },
  success: {
    main: '#22C55E',
    light: '#4ADE80',
    dark: '#16A34A',
  },
  error: {
    main: '#EF4444',
    light: '#F87171',
    dark: '#DC2626',
  },
  info: {
    main: '#06B6D4',
    light: '#22D3EE',
    dark: '#0891B2',
  },
  glassmorphism: {
    light: {
      background: 'rgba(255, 255, 255, 0.05)',
      backdropFilter: 'blur(10px)',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
    },
    medium: {
      background: 'rgba(255, 255, 255, 0.08)',
      backdropFilter: 'blur(15px)',
      border: '1px solid rgba(255, 255, 255, 0.15)',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
    },
    strong: {
      background: 'rgba(30, 42, 120, 0.95)',
      backdropFilter: 'blur(20px)',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      boxShadow: '0 25px 50px rgba(0, 0, 0, 0.5)',
    },
  },
};

// Light theme colors
const lightColors = {
  primary: {
    main: '#06B6D4',
    light: '#0891B2',
    dark: '#0E7490',
  },
  secondary: {
    main: '#7C3AED',
    light: '#8B5CF6',
    dark: '#6D21A8',
  },
  accent: {
    main: '#D946EF',
    light: '#E879F9',
    dark: '#C026D3',
  },
  background: {
    main: '#ffffff',
    glass: '#ffffff',
    glassHover: '#fefefe',
    glassStrong: '#f5f5f5',
  },
  text: {
    primary: 'rgba(0, 0, 0, 0.87)',
    secondary: 'rgba(0, 0, 0, 0.6)',
    muted: 'rgba(0, 0, 0, 0.5)',
    disabled: 'rgba(0, 0, 0, 0.3)',
  },
  border: {
    light: 'rgba(0, 0, 0, 0.1)',
    medium: 'rgba(0, 0, 0, 0.15)',
    strong: 'rgba(0, 0, 0, 0.2)',
  },
  gradient: {
    primary: 'linear-gradient(90deg, #3B82F6 0%, #1D4ED8 48%, #06B6D4 100%)',
    primaryHover: 'linear-gradient(90deg, #2563EB 0%, #1E40AF 48%, #0891B2 100%)',
  },
  warning: {
    main: '#F59E0B',
    light: '#FBBF24',
    dark: '#D97706',
  },
  success: {
    main: '#22C55E',
    light: '#4ADE80',
    dark: '#16A34A',
  },
  error: {
    main: '#EF4444',
    light: '#F87171',
    dark: '#DC2626',
  },
  info: {
    main: '#06B6D4',
    light: '#22D3EE',
    dark: '#0891B2',
  },
  glassmorphism: {
    light: {
      background: 'rgba(255, 255, 255, 0.7)',
      backdropFilter: 'blur(10px)',
      border: '1px solid rgba(0, 0, 0, 0.1)',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
    },
    medium: {
      background: 'rgba(255, 255, 255, 0.8)',
      backdropFilter: 'blur(15px)',
      border: '1px solid rgba(0, 0, 0, 0.15)',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
    },
    strong: {
      background: 'rgba(255, 255, 255, 0.95)',
      backdropFilter: 'blur(20px)',
      border: '1px solid rgba(0, 0, 0, 0.2)',
      boxShadow: '0 25px 50px rgba(0, 0, 0, 0.2)',
    },
  },
};

// Theme factory function
export const createAppTheme = (isDarkMode = true) => {
  const customColors = isDarkMode ? darkColors : lightColors;
  
  // Custom component styles
const componentOverrides = {
  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: 8,
        textTransform: 'none',
        fontWeight: 600,
        transition: 'all 0.3s ease',
      },
      contained: {
        background: customColors.gradient.primary,
        color: 'white',
        '&:hover': {
          background: customColors.gradient.primaryHover,
          transform: 'translateY(-2px)',
          boxShadow: '0 8px 25px rgba(0, 0, 0, 0.3)',
        },
        '&:disabled': {
          background: customColors.background.glassStrong,
          color: customColors.text.disabled,
        },
      },
      outlined: {
        borderColor: isDarkMode ? customColors.border.medium : 'rgba(0, 0, 0, 0.3)',
        color: customColors.text.primary,
        backgroundColor: isDarkMode ? 'transparent' : 'rgba(255, 255, 255, 0.8)',
        '&:hover': {
          borderColor: isDarkMode ? customColors.border.strong : 'rgba(0, 0, 0, 0.5)',
          background: isDarkMode ? customColors.background.glassHover : 'rgba(0, 0, 0, 0.1)',
        },
      },
      text: {
        color: customColors.text.secondary,
        '&:hover': {
          background: customColors.background.glassHover,
        },
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        background: customColors.background.glass,
        backdropFilter: 'blur(10px)',
        border: `1px solid ${customColors.border.light}`,
        color: customColors.text.primary,
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        background: customColors.background.glass,
        backdropFilter: 'blur(10px)',
        border: `1px solid ${customColors.border.light}`,
        color: customColors.text.primary,
      },
    },
  },
  MuiTextField: {
    styleOverrides: {
      root: {
        '& .MuiOutlinedInput-root': {
          color: customColors.text.primary,
          background: customColors.background.glass,
          '& fieldset': {
            borderColor: customColors.border.medium,
          },
          '&:hover fieldset': {
            borderColor: customColors.border.strong,
          },
          '&.Mui-focused fieldset': {
            borderColor: customColors.primary.main,
          },
        },
        '& .MuiInputLabel-root': {
          color: customColors.text.secondary,
          '&.Mui-focused': {
            color: customColors.primary.main,
          },
        },
      },
    },
  },
  MuiSelect: {
    styleOverrides: {
      root: {
        color: customColors.text.primary,
        background: customColors.background.glass,
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: customColors.border.medium,
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: customColors.border.strong,
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: customColors.primary.main,
        },
        '& .MuiSvgIcon-root': {
          color: customColors.text.secondary,
        },
        '& .MuiSelect-select': {
          background: 'transparent',
        },
      },
    },
  },
  MuiMenuItem: {
    styleOverrides: {
      root: {
        color: customColors.text.primary,
        '&:hover': {
          background: customColors.background.glassHover,
        },
        '&.Mui-selected': {
          background: `${customColors.primary.main}20`,
          '&:hover': {
            background: `${customColors.primary.main}30`,
          },
        },
      },
    },
  },
  MuiTable: {
    styleOverrides: {
      root: {
        background: 'transparent',
      },
    },
  },
  MuiTableHead: {
    styleOverrides: {
      root: {
        '& .MuiTableCell-root': {
          color: customColors.text.primary,
          fontWeight: 600,
          borderBottom: `1px solid ${customColors.border.light}`,
          background: customColors.background.glassHover,
        },
      },
    },
  },
  MuiTableBody: {
    styleOverrides: {
      root: {
        '& .MuiTableRow-root': {
          '&:nth-of-type(even)': {
            background: customColors.background.glass,
          },
          '&:nth-of-type(odd)': {
            background: customColors.background.glassHover,
          },
          '&:hover': {
            background: customColors.background.glassStrong,
          },
        },
        '& .MuiTableCell-root': {
          color: customColors.text.primary,
          borderBottom: `1px solid ${customColors.border.light}`,
        },
      },
    },
  },
  MuiDialog: {
    styleOverrides: {
      paper: {
        background: customColors.glassmorphism.strong.background,
        backdropFilter: customColors.glassmorphism.strong.backdropFilter,
        border: customColors.glassmorphism.strong.border,
        boxShadow: customColors.glassmorphism.strong.boxShadow,
        color: customColors.text.primary,
      },
    },
  },
  MuiDialogTitle: {
    styleOverrides: {
      root: {
        background: customColors.background.glassStrong,
        borderBottom: `1px solid ${customColors.border.light}`,
        color: customColors.text.primary,
      },
    },
  },
  MuiDialogActions: {
    styleOverrides: {
      root: {
        background: customColors.background.glass,
        borderTop: `1px solid ${customColors.border.light}`,
        padding: 16,
      },
    },
  },
  MuiAlert: {
    styleOverrides: {
      root: {
        background: 'rgba(211, 47, 47, 0.1)',
        border: '1px solid rgba(211, 47, 47, 0.3)',
        color: '#ffcdd2',
      },
      standardSuccess: {
        background: 'rgba(34, 197, 94, 0.1)',
        border: '1px solid rgba(34, 197, 94, 0.3)',
        color: '#bbf7d0',
      },
      standardInfo: {
        background: 'rgba(6, 182, 212, 0.1)',
        border: '1px solid rgba(6, 182, 212, 0.3)',
        color: '#a5f3fc',
      },
      standardWarning: {
        background: 'rgba(245, 158, 11, 0.1)',
        border: '1px solid rgba(245, 158, 11, 0.3)',
        color: '#fed7aa',
      },
    },
  },
  MuiIconButton: {
    styleOverrides: {
      root: {
        '&:hover': {
          background: customColors.background.glassHover,
        },
      },
    },
  },
  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        background: customColors.glassmorphism.strong.background,
        backdropFilter: customColors.glassmorphism.strong.backdropFilter,
        border: customColors.glassmorphism.strong.border,
        color: customColors.text.primary,
        fontSize: '0.875rem',
      },
    },
  },
  
  // DatePicker calendar overrides
  MuiPickersCalendarHeader: {
    styleOverrides: {
      root: {
        background: isDarkMode ? customColors.background.glass : '#e8e8e8',
        color: customColors.text.primary,
        borderBottom: isDarkMode ? `1px solid ${customColors.border.medium}` : '1px solid rgba(0, 0, 0, 0.2)',
        '& .MuiPickersCalendarHeader-label': {
          color: customColors.text.primary,
        },
        '& .MuiIconButton-root': {
          color: customColors.text.primary,
          '&:hover': {
            background: isDarkMode ? customColors.background.glassHover : 'rgba(0, 0, 0, 0.1)',
          },
        },
      },
    },
  },
  MuiPickersDay: {
    styleOverrides: {
      root: {
        color: customColors.text.primary,
        '&:hover': {
          background: customColors.background.glassHover,
        },
        '&.Mui-selected': {
          background: customColors.primary.main,
          color: '#ffffff',
          '&:hover': {
            background: customColors.primary.dark,
          },
        },
        '&.MuiPickersDay-today': {
          border: `1px solid ${customColors.primary.main}`,
        },
      },
    },
  },
  MuiCalendarPicker: {
    styleOverrides: {
      root: {
        background: isDarkMode ? customColors.background.glass : '#f8f9fa !important',
        color: customColors.text.primary,
        '& .MuiDayPicker-header': {
          '& .MuiTypography-root': {
            color: isDarkMode ? customColors.text.secondary : '#495057 !important',
          },
        },
        '& .MuiPickersDay-dayOutsideMonth': {
          color: isDarkMode ? customColors.text.disabled : '#adb5bd !important',
        },
      },
    },
  },
  MuiDateCalendar: {
    styleOverrides: {
      root: {
        background: isDarkMode ? customColors.background.glass : '#f8f9fa !important',
        color: customColors.text.primary,
        border: isDarkMode ? `1px solid ${customColors.border.medium}` : '1px solid #dee2e6 !important',
        borderRadius: '8px',
        boxShadow: isDarkMode 
          ? '0 4px 20px rgba(0, 0, 0, 0.3)' 
          : '0 4px 20px rgba(0, 0, 0, 0.15) !important',
        '& .MuiDayCalendar-header': {
          '& .MuiTypography-root': {
            color: isDarkMode ? customColors.text.secondary : '#495057 !important',
            fontWeight: 600,
          },
        },
        '& .MuiPickersDay-root': {
          color: isDarkMode ? customColors.text.primary : '#212529 !important',
          backgroundColor: isDarkMode ? 'transparent' : '#ffffff !important',
          border: isDarkMode ? 'none' : '1px solid #e9ecef !important',
          '&:hover': {
            background: isDarkMode ? customColors.background.glassHover : '#e9ecef !important',
          },
          '&.Mui-selected': {
            background: customColors.primary.main,
            color: '#ffffff !important',
            '&:hover': {
              background: customColors.primary.dark,
            },
          },
          '&.MuiPickersDay-today': {
            border: `2px solid ${customColors.primary.main} !important`,
            fontWeight: 700,
          },
        },
        '& .MuiPickersDay-dayOutsideMonth': {
          color: isDarkMode ? customColors.text.disabled : '#adb5bd !important',
        },
      },
    },
  },
  MuiPickersPopper: {
    styleOverrides: {
      paper: {
        background: isDarkMode ? customColors.background.glass : '#f8f9fa !important',
        backdropFilter: isDarkMode ? 'blur(10px)' : 'none',
        border: isDarkMode ? `1px solid ${customColors.border.medium}` : '1px solid #dee2e6 !important',
        borderRadius: '8px',
        boxShadow: isDarkMode 
          ? '0 8px 32px rgba(0, 0, 0, 0.5)' 
          : '0 8px 32px rgba(0, 0, 0, 0.2) !important',
      },
    },
  },
};

  // Create the theme
  return createTheme({
    palette: {
      mode: isDarkMode ? 'dark' : 'light',
      primary: customColors.primary,
      secondary: customColors.secondary,
      background: {
        default: customColors.background.main,
        paper: customColors.background.glass,
      },
      text: {
        primary: customColors.text.primary,
        secondary: customColors.text.secondary,
      },
    },
    typography: {
      fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      h4: {
        fontWeight: 700,
        textShadow: isDarkMode ? '0 2px 4px rgba(0, 0, 0, 0.3)' : '0 2px 4px rgba(0, 0, 0, 0.1)',
      },
      h6: {
        fontWeight: 600,
      },
    },
    shape: {
      borderRadius: 8,
    },
    components: componentOverrides,
  });
};

// Default theme (dark)
const theme = createAppTheme(true);

export default theme;
export { darkColors, lightColors, commonStyles };
